import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBell,
  FaEnvelope,
  FaUser,
  FaSignOutAlt,
  FaKey,
  FaUserCircle,
  FaRegCopy,
  FaKeyboard,
} from "react-icons/fa";
// import { BsGrid3X3Gap } from "react-icons/bs";
import moment from "moment";
import styles from "./UserHeaderTop.module.css";
import { TokenService, AxiosInstance } from "../../services/Api";
import { getRoleDisplayName } from "./menuConfig";
import BlobImage from "../../components/BlobImage/BlobImage";
import { PLAN, getPlanFeature } from "../../utils/access";
import { useAuth } from "../../hooks/useAuth";
import { useNotifications } from "../../hooks/useNotifications";
import { clearFcmToken } from "../../firebase";
import { switchLanguage } from "../../redux/languageSlice";
import { useDispatch, useSelector } from "react-redux";
import TamilKeyBoard from "../../components/TamilKeyBoard/TamilKeyBoard";

const UserHeaderTop = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);

  // ---- Tamil Keyboard State (ADDED) ----
  const [showTamilKeyboard, setShowTamilKeyboard] = useState(false);

  // State
  const [showNotification, setShowNotification] = useState(false);
  const [showMessageNotification, setShowMessageNotification] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const profileRef = useRef(null);
  const clientModalRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const userData = TokenService.getUser() || {};
  const user = TokenService.getUser();
  const userId = user?.id || 0;
  const clientData = user?.client_data || [];
  const roleId = user?.role?.id || "";
  const roleName = getRoleDisplayName(roleId);
  const userName = userData.name || userData.userName || "User";
  const userAvatar = userData.avatar || userData.profileImage || null;
  const lastLogin = userData.lastLogin || new Date().toLocaleString();
  const licenseCode = userData.licenseCode || "BCPL - 2025000";

  // License details come from the plan stored in the user cookie.
  // A user holds a single plan, so plan_details[0] is the licence.
  const planDetail = user?.plan_details?.[0] || null;
  const licenseType = planDetail
    ? planDetail.plan_name ||
      (getPlanFeature(user) === PLAN.CORE_AI ? "Core + AI" : "Core")
    : "N/A";

  const firstClient = clientData.length > 0 ? clientData[0] : null;

  // Profile photo — fetched LIVE via the existing profile API so a newly
  // uploaded photo shows for ALL roles (admin/staff/student/parent)
  // without re-login. Cookie snapshot is the fallback until it loads.
  // The profile page fires a "profilePhotoUpdated" window event after a
  // successful save so the header refreshes immediately.
  const [profile_photo, setProfilePhoto] = useState(
    firstClient?.profile_photo || firstClient?.profile_original_name || null,
  );
  useEffect(() => {
    const userTypeByRole = { 2: "admin", 5: "admin", 3: "staff", 4: "student", 6: "parent" };
    const userType = userTypeByRole[Number(roleId)] || "";
    const fetchProfilePhoto = async () => {
      try {
        const res = await AxiosInstance.post(`/groupCockpit/profile/${userId}`, {
          user_type: userType,
        });
        if (res.data?.success && res.data.data) {
          setProfilePhoto(
            res.data.data.profile_photo ||
              res.data.data.profile_original_name ||
              null,
          );
        }
      } catch (err) {
        // keep the cookie value on failure
      }
    };
    if (userId && userType) fetchProfilePhoto();
    const onPhotoUpdated = () => {
      if (userId && userType) fetchProfilePhoto();
    };
    window.addEventListener("profilePhotoUpdated", onPhotoUpdated);
    return () =>
      window.removeEventListener("profilePhotoUpdated", onPhotoUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, roleId]);

  // School logo — fetched LIVE from campus_school_master so a newly
  // uploaded logo shows without re-login (cookie value is the fallback
  // until the API responds).
  const [school_logo, setSchoolLogo] = useState(
    firstClient?.school_logo || null,
  );
  useEffect(() => {
    const fetchSchoolLogo = async () => {
      try {
        const res = await AxiosInstance.get(
          `groupCockpit/school-logo/${firstClient.school_id}`,
        );
        if (res.data?.success) {
          setSchoolLogo(res.data.data?.school_logo || null);
        }
      } catch (err) {
        // keep the cookie value on failure
      }
    };
    if (firstClient?.school_id) fetchSchoolLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstClient?.school_id]);

  // Notifications: Eventlist history + live Firebase Cloud Messaging pushes.
  const { notifications, notificationCount } = useNotifications({
    clientId: firstClient?.client_id,
    schoolId: firstClient?.school_id,
    userId: userId,
    role: roleId,
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const fetchReceivedMessages = async () => {
    try {
      const response = await AxiosInstance.post("/message/getMessage", {
        user_id: String(userId),
      });
      if (response.data?.status && Array.isArray(response.data.data)) {
        const formatted = response.data.data.map((msg) => ({
          id: msg.id,
          subject: msg.subject || "",
          body: msg.messages || "",
          senderInitial: msg.subject?.charAt(0)?.toUpperCase() || "M",
          time: moment
            .utc(msg.created_at)
            .utcOffset("+05:30")
            .format("DD MMM YYYY, hh:mm A"),
          attached_file: msg.attached_file || null,
          original_file: msg.original_file || null,
          isRead: msg.read_at === 1 || msg.read_at === "1",
        }));
        setMessages(formatted);
        const unreadCount = response.data.data.filter(
          (msg) => msg.read_at === 0 || msg.read_at === "0",
        ).length;
        setUnreadMessageCount(unreadCount);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  useEffect(() => {
    if (userId) fetchReceivedMessages();
  }, [userId]);

  // ---- Notifications now handled by useNotifications() (Eventlist history + FCM live pushes). ----
  // The old inline polling fetch has been replaced by the hook above.
  // const fetchNotifications = async () => {
  //   try {
  //     const response = await AxiosInstance.post("/school/Eventlist", {
  //       client_id: firstClient?.client_id,
  //     });
  //     if (response.data?.status && Array.isArray(response.data.data)) {
  //       const formatted = response.data.data.map((event) => ({
  //         id: event.id,
  //         subject: event.event_subject || "",
  //         description: event.event_description || "",
  //         venue: event.event_venue || "",
  //         startDate: moment(event.start_date).format("DD MMM YYYY"),
  //         time: moment(event.start_date).fromNow(),
  //       }));
  //       setNotifications(formatted);
  //       setNotificationCount(formatted.length);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching notifications:", err);
  //   }
  // };

  // useEffect(() => {
  //   if (firstClient?.client_id) fetchNotifications();
  // }, [firstClient?.client_id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleProfileClick = () => setShowProfileDropdown(!showProfileDropdown);
  const handleNavigateProfile = () => {
    setShowProfileDropdown(false);
    navigate("/user/profile");
  };
  const handleChangePassword = () => {
    setShowProfileDropdown(false);
    navigate("/user/change-password");
  };
  const handleChangeContentManagement = () => {
    setShowProfileDropdown(false);
    navigate("/user/content-management");
  };
  const handleLogout = async () => {
    setShowProfileDropdown(false);
    try {
      // Delete the FCM token first so the next user on this machine does not
      // inherit this user's push subscription.
      await clearFcmToken().catch(() => {});
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login", { replace: true });
    } finally {
      setShowProfileDropdown(false);
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowClientModal(true);
  };
  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setShowClientModal(false), 150);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const { language, translations } = useSelector((state) => state.language);
  const dispatch = useDispatch();
  const languageOptions = [
    { code: "ta", label: "TA" },
    { code: "en", label: "EN" },
  ];

  const handleLanguageChange = (e) => {
    const langCode = e.target.value;
    dispatch(switchLanguage(langCode));
    localStorage.setItem("appLanguage", langCode);
    window.location.reload();
  };

  const tamilKeyboardLabel =
    translations?.labels?.tamilKeyboard || "தமிழ் விசைப்பலகை";

  return (
    <header className={styles.headerTop}>
      <div className={styles.headerContainer}>
        {/* Left Side */}
        <div className={styles.headerLeft}>
          <div
            className={styles.logoWrapper}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={clientModalRef}
          >
            <h1 className={styles.logoTitle}>
              {firstClient
                ? `${firstClient.school_name} - ${firstClient.client_id}`
                : licenseCode}
            </h1>

            {showClientModal && firstClient && (
              <div
                className={styles.clientModal}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className={styles.clientModalContent}>
                  <div className={styles.clientTopSection}>
                    <div className={styles.defaultAvatar}>
                      <BlobImage
                        path={school_logo}
                        fallback="https://cdn-icons-png.flaticon.com/512/2602/2602414.png"
                        alt="School Logo"
                        className={styles.schoolIcon}
                      />
                    </div>
                    <h3 className={styles.clientSchoolName}>
                      {firstClient.school_name || "School Name"}
                    </h3>
                  </div>
                  <div className={styles.clientDetailsSection}>
                    <div className={styles.clientInfoRow}>
                      <span className={styles.clientLabel}>Client ID</span>
                      <span className={styles.clientValue}>
                        {firstClient.client_id}
                      </span>
                    </div>
                    <div className={styles.clientInfoRow}>
                      <span className={styles.clientLabel}>Plan Name</span>
                      <span className={styles.clientValue}>{licenseType}</span>
                    </div>
                    <div className={styles.clientInfoRow}>
                      <span className={styles.clientLabel}>Valid upto</span>
                      <span className={styles.clientValue}>
                        {formatDate(planDetail?.expire_date)}
                      </span>
                    </div>
                    <div className={styles.clientInfoRow}>
                      <span className={styles.clientLabel}>Address</span>
                      <span className={styles.clientValue}>
                        {firstClient.full_address}
                        {firstClient.city && `, ${firstClient.city}`}
                        {firstClient.name && `, ${firstClient.name}`}
                        {firstClient.postal_no && `, ${firstClient.postal_no}`}
                      </span>
                    </div>
                    <div className={styles.clientInfoRow}>
                      <span className={styles.clientLabel}>Contact</span>
                      <span className={styles.clientValue}>
                        {firstClient.client_mobile}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side */}
        <div className={styles.headerRight}>
          {/* ==================== Tamil Keyboard Toggle (ADDED) ==================== */}
          <button
            type="button"
            onClick={() => setShowTamilKeyboard((prev) => !prev)}
            className={`${styles.tamilKeyboardBtn} ${
              showTamilKeyboard ? styles.tamilKeyboardBtnActive : ""
            }`}
            title={tamilKeyboardLabel}
          >
            <FaKeyboard size={14} />
            {tamilKeyboardLabel}
          </button>
          <div className={styles.iconWrapper}>
            <img
              src="https://flagcdn.com/w40/in.png"
              alt="India"
              className={styles.flagIcon}
            />
          </div>
          <div className={styles.languageSection}>
            <select
              value={language}
              onChange={handleLanguageChange}
              className={styles.languageDropdown}
            >
              {languageOptions.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notifications */}
          <div
            className={styles.iconWrapper}
            onMouseEnter={() => setShowNotification(true)}
            onMouseLeave={() => setShowNotification(false)}
            style={{ cursor: "pointer" }}
          >
            <FaBell className={styles.icon} />
            {notificationCount > 0 && (
              <span className={styles.badge}>{notificationCount}</span>
            )}
            {showNotification && (
              <div className={styles.notificationDropdown}>
                <div className={styles.notificationHeader}>
                  {notificationCount} Notifications
                </div>
                {notifications.slice(0, 5).map((notif) => (
                  <div
                    key={notif.id}
                    className={styles.notificationItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (roleId === 5) navigate("/user/calenderList");
                      else navigate("/user/events");
                    }}
                  >
                    <div className={styles.notificationIcon}>📅</div>
                    <div>
                      <p className={styles.notificationTitle}>
                        {notif.subject}
                      </p>
                      <p className={styles.notificationTime}>
                        {notif.startDate} • {notif.venue}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          <div
            className={styles.iconWrapper}
            onMouseEnter={() => setShowMessageNotification(true)}
            onMouseLeave={() => setShowMessageNotification(false)}
            style={{ cursor: "pointer" }}
          >
            <FaEnvelope className={styles.icon} />
            {unreadMessageCount > 0 && (
              <span className={styles.badge}>{unreadMessageCount}</span>
            )}
            {showMessageNotification && (
              <div className={styles.notificationDropdown}>
                <div className={styles.notificationHeader}>
                  {unreadMessageCount} Messages
                </div>
                {messages
                  .filter((msg) => !msg.isRead)
                  .slice(0, 5)
                  .map((msg) => (
                    <div
                      key={msg.id}
                      className={styles.notificationItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/user/messaging/email");
                      }}
                    >
                      <div className={styles.notificationIcon}>
                        {msg.senderInitial}
                      </div>
                      <div>
                        <p className={styles.notificationTitle}>
                          {msg.subject}
                        </p>
                        <p className={styles.notificationTime}>{msg.time}</p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Profile */}
          <div
            className={styles.profileWrapper}
            ref={profileRef}
            onClick={handleProfileClick}
          >
            <div className={styles.profileIcon}>
              {userAvatar ? (
                <img
                  src={userAvatar}
                  alt={userName}
                  className={styles.profileImage}
                />
              ) : profile_photo ? (
                <BlobImage
                  path={profile_photo}
                  fallback=""
                  alt={userName}
                  className={styles.profileImage}
                />
              ) : (
                <FaUser className={styles.icon} />
              )}
            </div>

            {showProfileDropdown && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownAvatar}>
                    {userAvatar ? (
                      <img
                        src={userAvatar}
                        alt={userName}
                        className={styles.dropdownAvatarImage}
                      />
                    ) : (
                      <div className={styles.defaultAvatar}>
                        <BlobImage
                          path={profile_photo}
                          fallback="https://cdn-icons-png.flaticon.com/512/2602/2602414.png"
                          alt="Profile"
                          className={styles.schoolIcon}
                        />
                      </div>
                    )}
                  </div>
                  <div className={styles.dropdownUserInfo}>
                    <h4 className={styles.dropdownUserName}>{userName}</h4>
                    <p className={styles.dropdownUserRole}>{roleName}</p>
                    <p className={styles.dropdownLastLogin}>
                      {translations.labels.lastLogin}: {lastLogin}
                    </p>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <ul className={styles.dropdownMenu}>
                  <li
                    className={styles.dropdownItem}
                    onClick={handleNavigateProfile}
                  >
                    <FaUserCircle className={styles.dropdownItemIcon} />
                    <span>{translations.labels.profile}</span>
                  </li>
                  <li
                    className={styles.dropdownItem}
                    onClick={handleChangePassword}
                  >
                    <FaKey className={styles.dropdownItemIcon} />
                    <span>{translations.labels.changePassword}</span>
                  </li>
                  {roleId === 5 && (
                    <li
                      className={styles.dropdownItem}
                      onClick={handleChangeContentManagement}
                    >
                      <FaRegCopy className={styles.dropdownItemIcon} />
                      <span>{translations.labels.contentManagement}</span>
                    </li>
                  )}
                  <li
                    className={`${styles.dropdownItem} ${styles.logoutItem}`}
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt className={styles.dropdownItemIcon} />
                    <span>{translations.labels.logout}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== Floating Draggable Tamil Keyboard (ADDED) ==================== */}
      <TamilKeyBoard
        show={showTamilKeyboard}
        onClose={() => setShowTamilKeyboard(false)}
        title={tamilKeyboardLabel}
      />
    </header>
  );
};

export default UserHeaderTop;
